<template>
  <Modal :open="props.open" :custom-height="'lg:h-auto'" @close-modal="$emit('close-modal')">
    <template #modal-header>
      <div class="flex flex-col text-strongest">
        <div class="flex gap-2 items-center">
          <Icon name="CircleCheck" class="h-4 w-4 text-info" />
          <p class="font-inter text-sm text-info font-semibold">Complete investment</p>
        </div>
        <h3 class="font-semibold text-[20px] md:text-[28px]">
          {{ isIRA ? "Skip ahead to IRA details" : "Skip ahead to funding" }}
        </h3>
      </div>
    </template>
    <template #modal-body>
      <div class="p-4 md:p-6">
        <div class="bg-table-bg p-4 mb-2 rounded-[20px]">
          <p class="text-strong text-sm md:text-base font-medium font-inter">
            Good news — your details are already on file. You can review and edit anytime before
            submitting.
          </p>
        </div>

        <div class="bg-table-bg px-6 py-10 rounded-[20px] hidden md:block">
          <div class="relative flex items-start">
            <div
              class="absolute top-4 h-[2px] bg-outline-dividers z-0"
              :style="{
                left: `calc(${(0.5 / totalDesktopSteps) * 100}%)`,
                right: `calc(${(0.5 / totalDesktopSteps) * 100}%)`,
              }"
            ></div>
            <div
              class="absolute z-20 top-3 -translate-y-full"
              :style="{
                left: `calc(${(1 / totalDesktopSteps) * 100}%)`,
                transform: 'translateX(-50%) translateY(-100%)',
              }"
            >
              <div
                class="bg-bg-canvas font-inter border border-container-outline rounded-[7px] px-2 py-0.5 text-sm font-medium text-strongest whitespace-nowrap relative"
              >
                You are here
                <div
                  class="absolute left-1/2 -translate-x-1/2 w-0 h-0"
                  style="
                    bottom: -6px;
                    border-left: 6px solid transparent;
                    border-right: 6px solid transparent;
                    border-top: 6px solid var(--container-outline);
                  "
                ></div>
              </div>
            </div>

            <!-- Step 1: Investment info captured -->
            <div class="relative z-10 flex flex-col items-center text-center flex-1">
              <div class="w-8 h-8 rounded-full bg-success flex items-center justify-center mb-2">
                <Icon name="Check" class="h-4 w-4 text-white" :stroke-width="3.5" />
              </div>
              <p class="text-sm font-medium text-strongest font-inter">Investment info captured</p>
              <p class="text-xs font-medium text-strong font-inter mt-2">
                {{
                  selectedOffering?.type === "Reg D" ? "Regulation D" : "Registered Offering (S1)"
                }}
                • {{ formatCurrency(principal) }}
              </p>
              <p class="text-xs font-medium text-strong font-inter">
                {{ selectedOffering?.interest_rate }}% interest •
                {{ formatTerm(selectedOffering?.bond_term) }}
              </p>
            </div>

            <!-- Step 2: Personal info on file -->
            <div class="relative z-10 flex flex-col items-center text-center flex-1">
              <div class="w-8 h-8 rounded-full bg-success flex items-center justify-center mb-2">
                <Icon name="Check" class="h-4 w-4 text-white" :stroke-width="3.5" />
              </div>
              <p class="text-sm font-medium text-strongest font-inter">Personal info on file</p>
            </div>

            <!-- Step 3: Questionnaire completed -->
            <div class="relative z-10 flex flex-col items-center text-center flex-1">
              <div class="w-8 h-8 rounded-full bg-success flex items-center justify-center mb-2">
                <Icon name="Check" class="h-4 w-4 text-white" :stroke-width="3.5" />
              </div>
              <p class="text-sm font-medium text-strongest font-inter">Questionnaire completed</p>
            </div>

            <!-- Conditional: Joint or Entity -->
            <div
              v-if="investmentType === 'Joint' || investmentType === 'Entity'"
              class="relative z-10 flex flex-col items-center text-center flex-1"
            >
              <div class="w-8 h-8 rounded-full bg-success flex items-center justify-center mb-2">
                <Icon name="Check" class="h-4 w-4 text-white" :stroke-width="3.5" />
              </div>
              <p class="text-sm font-medium text-strongest font-inter">
                {{ investmentType === "Joint" ? "Joint info on file" : "Entity info on file" }}
              </p>
            </div>

            <!-- Final Step -->
            <div class="relative z-10 flex flex-col items-center text-center flex-1">
              <div class="w-8 h-8 rounded-full bg-info flex items-center justify-center mb-2">
                <IconArrowRight class="h-4 w-4 ml-0.5" />
              </div>
              <p class="text-sm font-bold text-strongest font-inter">
                {{ isIRA ? "IRA Details" : "Funding (next)" }}
              </p>
            </div>
          </div>
        </div>

        <div class="bg-table-bg px-5 py-6 rounded-[20px] md:hidden">
          <div class="flex flex-col items-center">
            <!-- Step 1: Investment info captured -->
            <div class="flex flex-col items-center text-center">
              <div
                class="w-6 h-6 md:w-8 md:h-8 rounded-full bg-success flex items-center justify-center"
              >
                <Icon name="Check" class="h-4 w-4 text-white" :stroke-width="2.5" />
              </div>
              <p class="text-sm font-medium text-strongest font-inter mt-2">
                Investment info captured
              </p>
              <p class="text-xs font-medium text-strong font-inter mt-1">
                {{
                  selectedOffering?.type === "Reg D"
                    ? "Regulation D Offering"
                    : "Registered Offering (S1)"
                }}
                • {{ formatCurrency(principal) }} • {{ selectedOffering?.interest_rate }}% interest
                •
                {{ formatTerm(selectedOffering?.bond_term) }}
              </p>
            </div>
            <div
              class="relative flex flex-col items-center"
              :style="{ height: mobileFirstSegment + 'px' }"
            >
              <div class="w-[2px] bg-outline-dividers h-full my-1.5"></div>
              <div class="absolute top-1/2 -translate-y-1/2 left-[7px]">
                <div
                  class="bg-bg-canvas font-inter border border-container-outline rounded-[7px] px-2 py-0.5 text-sm font-medium text-strongest whitespace-nowrap relative"
                >
                  You are here
                  <div
                    class="absolute top-1/2 -translate-y-1/2 w-0 h-0"
                    style="
                      left: -6px;
                      border-top: 6px solid transparent;
                      border-bottom: 6px solid transparent;
                      border-right: 6px solid var(--container-outline);
                    "
                  ></div>
                </div>
              </div>
            </div>

            <!-- Step 2: Personal info on file -->
            <div class="flex flex-col items-center text-center">
              <div
                class="w-6 h-6 md:w-8 md:h-8 rounded-full bg-success flex items-center justify-center"
              >
                <Icon name="Check" class="h-4 w-4 text-white" :stroke-width="2.5" />
              </div>
              <p class="text-sm font-medium text-strongest font-inter mt-2">
                Personal info on file
              </p>
            </div>

            <!-- Short connector -->
            <div
              class="w-[2px] bg-outline-dividers my-1.5"
              :style="{ height: mobileShortSegment + 'px' }"
            ></div>

            <!-- Step 3: Questionnaire completed -->
            <div class="flex flex-col items-center text-center">
              <div
                class="w-6 h-6 md:w-8 md:h-8 rounded-full bg-success flex items-center justify-center"
              >
                <Icon name="Check" class="h-4 w-4 text-white" :stroke-width="2.5" />
              </div>
              <p class="text-sm font-medium text-strongest font-inter mt-2">
                Questionnaire completed
              </p>
            </div>

            <!-- Conditional: Joint or Entity -->
            <template v-if="investmentType === 'Joint' || investmentType === 'Entity'">
              <div
                class="w-[2px] bg-outline-dividers my-1.5"
                :style="{ height: mobileShortSegment + 'px' }"
              ></div>
              <div class="flex flex-col items-center text-center">
                <div
                  class="w-6 h-6 md:w-8 md:h-8 rounded-full bg-success flex items-center justify-center"
                >
                  <Icon name="Check" class="h-4 w-4 text-white" :stroke-width="2.5" />
                </div>
                <p class="text-sm font-medium text-strongest font-inter mt-2">
                  {{ investmentType === "Joint" ? "Joint info on file" : "Entity info on file" }}
                </p>
              </div>
            </template>

            <!-- Short connector -->
            <div
              class="w-[2px] bg-outline-dividers my-1.5"
              :style="{ height: mobileShortSegment + 'px' }"
            ></div>

            <!-- Final Step -->
            <div class="flex flex-col items-center text-center">
              <div
                class="w-6 h-6 md:w-8 md:h-8 rounded-full bg-info flex items-center justify-center"
              >
                <IconArrowRight class="h-3 w-3 md:h-4 md:w-4 ml-0.5" />
              </div>
              <p class="text-sm font-bold text-strongest font-inter mt-2">
                {{ isIRA ? "IRA Details" : "Funding (next)" }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="flex items-center justify-end gap-3">
        <Button
          size="lg-sm"
          variant="paper-primary"
          class="md:w-auto w-full"
          @click="($emit('next-step'), $emit('close-modal'))"
        >
          <template #label>Review details first</template>
        </Button>
        <Button
          size="lg-sm"
          variant="primary-secondary"
          class="md:w-auto w-full"
          @click="$emit('continue-investment')"
        >
          <template #icon>
            <Icon name="PartyPopper" class="h-5 w-5 mb-1" />
          </template>
          <template #label>Continue to {{ isIRA ? "IRA Details" : "Funding" }}</template>
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed } from "vue";
import { Modal, Icon, Button } from "@/components";
import { IconArrowRight } from "@/components/icons";
import { usePurchaseFlowStore } from "@/stores";
import { isRetirementAccountType } from "@/utilities";

defineEmits(["close-modal", "continue-investment", "next-step"]);

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
});

const purchaseFlowStore = usePurchaseFlowStore();

const selectedOffering = computed(() => purchaseFlowStore.selected_offering);
const investment = computed(() => purchaseFlowStore.current_investment);
const investmentType = computed(() => investment.value?.type);
const isIRA = computed(() => isRetirementAccountType(investmentType.value));

const principal = computed(() => {
  const raw = investment.value?.amount;
  return raw ? Number(String(raw).replace(/,/g, "")) : 0;
});

// Total number of desktop steps (used for percentage-based line positioning)
const totalDesktopSteps = computed(() => {
  let count = 4; // base: investment, personal, questionnaire, funding/IRA
  if (investmentType.value === "Joint" || investmentType.value === "Entity") count++;
  return count;
});

// Mobile vertical spacing (px)
const mobileFirstSegment = 56;
const mobileShortSegment = 18;

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatTerm(months) {
  if (!months) return "";
  if (months % 12 === 0) return `${months / 12} year`;
  return `${months}mo`;
}
</script>
